import type { ComponentType } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { CodeAccessTab } from '../components/admin/CodeAccessTab';
import { MyAccessTab } from '../components/admin/MyAccessTab';
import { ProxyAdminTab } from '../components/admin/ProxyAdminTab';
import { PageHeader } from '../components/PageHeader';
import { useAuthContext } from '../contexts/AuthContext';
import { useProxyAdminAccess } from '../hooks/useProxyAdmin';

/**
 * Who may open a tab. `hawk-admin` tabs follow Hawk's admin flag; `proxy-admin`
 * follows middleman's own verdict, since it decides who administers it and its
 * rules differ from Hawk's.
 */
type Access = 'everyone' | 'hawk-admin' | 'proxy-admin';

/**
 * Settings sections, one route each: everyone's own first, then the admin
 * group. Adding a section is one entry here plus an API that checks admin
 * itself; an admin tab shows only to those its API serves, and never mounts for
 * anyone else, so its requests don't fire.
 */
const TABS = [
  { key: 'access', label: 'My access', access: 'everyone', Tab: MyAccessTab },
  {
    key: 'code-access',
    label: 'Code access',
    access: 'hawk-admin',
    Tab: CodeAccessTab,
  },
  {
    key: 'llm-proxy',
    label: 'LLM proxy',
    access: 'proxy-admin',
    Tab: ProxyAdminTab,
  },
] as const satisfies readonly {
  key: string;
  label: string;
  access: Access;
  Tab: ComponentType;
}[];

const DENIED: Record<Exclude<Access, 'everyone'>, string> = {
  'hawk-admin': 'Only Hawk admins can open this section.',
  'proxy-admin': 'Only LLM proxy (middleman) admins can open this section.',
};

export default function SettingsPage() {
  const { tab = 'access' } = useParams<{ tab?: string }>();
  const { user } = useAuthContext();
  const isAdmin = user?.isAdmin ?? false;
  const { access: proxyAccess, retry: retryAccess } = useProxyAdminAccess();
  const current = TABS.find(t => t.key === tab);
  if (!current) return <Navigate to="/settings" replace />;

  const allowed = (access: Access) =>
    access === 'everyone' ||
    (access === 'hawk-admin' && isAdmin) ||
    // A Hawk admin still gets the tab when middleman can't answer, to see why.
    (access === 'proxy-admin' &&
      (proxyAccess === 'admin' || (proxyAccess === 'unknown' && isAdmin)));
  const link = (t: (typeof TABS)[number]) => (
    <Link
      key={t.key}
      to={`/settings/${t.key}`}
      aria-current={t === current ? 'page' : undefined}
    >
      {t.label}
    </Link>
  );
  const adminTabs = TABS.filter(
    t => t.access !== 'everyone' && allowed(t.access)
  );

  return (
    <div className="pane flex flex-col md:h-full md:overflow-hidden">
      <PageHeader title="Settings">
        <nav className="seg flex-wrap" aria-label="Settings sections">
          {TABS.filter(t => t.access === 'everyone').map(link)}
        </nav>
        {adminTabs.length > 0 && (
          <>
            <span className="pane-title ml-4">Admin</span>
            <nav className="seg flex-wrap" aria-label="Admin sections">
              {adminTabs.map(link)}
            </nav>
          </>
        )}
      </PageHeader>
      <div className="md:min-h-0 md:flex-1 md:overflow-y-auto">
        {current.access === 'everyone' || allowed(current.access) ? (
          <current.Tab />
        ) : current.access === 'proxy-admin' && proxyAccess === 'checking' ? (
          <p className="m-4 text-xs text-muted">Checking access…</p>
        ) : current.access === 'proxy-admin' && proxyAccess === 'unknown' ? (
          // A failed check isn't a denial: say so, and let them try again.
          <p className="callout m-4">
            Couldn’t check whether you can manage the LLM proxy; middleman may
            be unreachable.{' '}
            <button type="button" className="row-action" onClick={retryAccess}>
              Try again
            </button>
          </p>
        ) : (
          <p className="callout m-4">{DENIED[current.access]}</p>
        )}
      </div>
    </div>
  );
}
